import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, FormControl, MenuItem, Paper, Select, Stack, Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';

import AppToast from '../../../shared/components/AppToast';
import AuditHistoryDialog from '../../../shared/components/AuditHistoryDialog';
import DataState from '../../../shared/components/DataState';
import { FILTER_ALL, SCOPE_LABEL } from '../../../shared/constants';
import useBeforeUnloadWarning from '../../../shared/hooks/useBeforeUnloadWarning';
import { resolveUserFacingError } from '../../../shared/utils/actionFeedback';
import { activityDisplayName } from '../../../shared/utils/domain';
import { usePagePresentation } from '../../../context/PagePresentationContext';
import useEmissionFactorMaster from '../hooks/useEmissionFactorMaster';
import { getCategoryDisplay } from '../model/categoryDisplay';
import { isActiveSyncStatus } from '../model/emissionFactorSyncRecovery';
import EmissionFactorFormDialog from './EmissionFactorFormDialog';
import EmissionFactorSyncDialog from './EmissionFactorSyncDialog';
import EmissionFactorSyncHistoryDialog from './EmissionFactorSyncHistoryDialog';
import EmissionFactorTable from './EmissionFactorTable';
import EmissionFactorToolbar from './EmissionFactorToolbar';

export default function EmissionFactorPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    factors,
    suppliers,
    supplierLoadError,
    filters,
    setFilter,
    options,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    loading,
    loadError,
    reload,
    saving,
    syncing,
    syncOperation,
    syncMonitoringError,
    syncPolicy,
    saveFactor,
    updateFactorStatus,
    syncFactors,
    retrySyncStatus,
    downloadSource,
    loadHistory,
    historyFor,
    syncHistory,
    syncHistoryLoading,
    loadSyncHistory,
  } = useEmissionFactorMaster();
  const [downloadingSourceId, setDownloadingSourceId] = useState(null);
  const downloadingSourceRef = useRef(null);
  usePagePresentation({ loading: loading || Boolean(downloadingSourceId) });
  const [toast, setToast] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [historyFactor, setHistoryFactor] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [syncHistoryOpen, setSyncHistoryOpen] = useState(false);
  const isSyncProcessing = syncing
    || Boolean(syncMonitoringError)
    || isActiveSyncStatus(syncOperation?.status);
  useBeforeUnloadWarning(isSyncProcessing);

  useEffect(() => {
    if (syncOpen && isSyncProcessing && syncOperation) setSyncResult(syncOperation);
  }, [isSyncProcessing, syncOpen, syncOperation]);

  const openSyncDialog = () => {
    setSyncResult(isSyncProcessing ? syncOperation : null);
    setSyncOpen(true);
    if (!syncHistoryLoading) loadSyncHistory().catch(() => {});
  };

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (factor) => { setEditing(factor); setFormOpen(true); };

  const handleSave = async (payload) => {
    const action = editing ? 'update' : 'create';
    try {
      await saveFactor(editing, payload);
      setFormOpen(false);
      setToast({
        severity: 'success',
        message: t('emissionFactor.toast.manualUpdateSuccess'),
      });
    } catch (error) {
      if (error?.fieldErrors?.length) {
        setToast(null);
        throw error;
      }
      setToast({
        severity: 'error',
        message: resolveUserFacingError(
          error,
          t(`common.feedback.${action}.failure`),
        ),
      });
    }
  };

  const handleSync = async (fiscalYear, options) => {
    try {
      const summary = await syncFactors(fiscalYear, options);
      setSyncResult(summary);
      loadSyncHistory().catch(() => {});
    } catch (error) {
      setToast({
        severity: 'error',
        message: resolveUserFacingError(error, t('common.feedback.sync.failure')),
      });
    }
  };

  const handleStatusChange = async (factor, status, reason) => {
    try {
      const { factor: updated, listRefreshFailed } = await updateFactorStatus(factor, status, reason);
      if (editing?.id === factor.id) setEditing(updated);
      setToast({
        severity: listRefreshFailed ? 'warning' : 'success',
        message: t(listRefreshFailed
          ? 'emissionFactor.toast.statusUpdatedRefreshFailed'
          : 'emissionFactor.toast.statusUpdated'),
      });
    } catch (error) {
      setToast({
        severity: 'error',
        message: resolveUserFacingError(error, t('emissionFactor.toast.statusUpdateFailed')),
      });
      throw error;
    }
  };

  const handleRetrySyncStatus = async () => {
    try {
      const summary = await retrySyncStatus();
      if (summary) setSyncResult(summary);
    } catch (error) {
      setToast({
        severity: 'error',
        message: resolveUserFacingError(error, t('common.feedback.sync.failure')),
      });
    }
  };

  // Source downloads are authenticated; private Blob URLs stay in the backend.
  const handleSourceClick = async (factorOrSource) => {
    const source = factorOrSource.source_file || factorOrSource;
    if (!source?.id || downloadingSourceRef.current) return;

    downloadingSourceRef.current = source.id;
    setDownloadingSourceId(source.id);
    setToast(null);
    try {
      await downloadSource(factorOrSource);
    } catch (error) {
      setToast({
        severity: 'error',
        message: resolveUserFacingError(error, t('emissionFactor.toast.downloadFailed')),
      });
    } finally {
      if (downloadingSourceRef.current === source.id) downloadingSourceRef.current = null;
      setDownloadingSourceId((current) => (current === source.id ? null : current));
    }
  };

  // Fetch the correction chain only when history is opened.
  const handleHistory = async (factor) => {
    setHistoryFactor(factor);
    setHistoryOpen(true);
    try {
      await loadHistory(factor);
    } catch (error) {
      setToast({
        severity: 'error',
        message: resolveUserFacingError(error, t('emissionFactor.toast.historyLoadFailed')),
      });
    }
  };

  const handleSyncHistory = async () => {
    setSyncHistoryOpen(true);
    try {
      await loadSyncHistory();
    } catch (error) {
      setToast({
        severity: 'error',
        message: resolveUserFacingError(error, t('emissionFactor.toast.syncHistoryLoadFailed')),
      });
    }
  };

  const historyCategory = historyFactor
    ? getCategoryDisplay(t, historyFactor.category)
    : null;
  const historyContext = historyFactor ? {
    scope: SCOPE_LABEL[historyFactor.scope] || historyFactor.scope,
    categoryShort: historyCategory.short,
    categoryLabel: historyCategory.label,
    activity: activityDisplayName(historyFactor),
    externalId: historyFactor.ef_external_id,
    fiscalYear: historyFactor.fiscal_year,
  } : null;

  return (
    <Box component="section" aria-labelledby="emission-factor-title" className="page-stack">
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
        >
          <Stack spacing={0.5} sx={{ minWidth: 0 }}>
            <Typography id="emission-factor-title" component="h1" variant="h6" sx={{ fontWeight: 800 }}>
              {t('emissionFactor.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
              {t('emissionFactor.subtitle')}
            </Typography>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={filters.fiscalYear}
                onChange={(event) => setFilter('fiscalYear', event.target.value)}
                inputProps={{ 'aria-label': t('emissionFactor.filter.fiscalYear') }}
              >
                <MenuItem value={FILTER_ALL}>{t('emissionFactor.filter.allFiscalYears')}</MenuItem>
                {options.fiscalYears.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<HistoryRoundedIcon />}
              onClick={handleSyncHistory}
            >
              {t('emissionFactor.sync.historyAction')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<SyncRoundedIcon />}
              onClick={openSyncDialog}
            >
              {t('emissionFactor.sync.action')}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={openCreate}
            >
              {t('emissionFactor.addFactor')}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <DataState
        loading={loading}
        error={loadError}
        onRetry={reload}
        errorMessage={t('emissionFactor.toast.loadFailed')}
      />

      <Alert
        severity="info"
        sx={{ mb: 2 }}
        action={(
          <Button color="inherit" size="small" onClick={() => navigate('/data-input')}>
            {t('emissionFactor.sync.goToDataInput')}
          </Button>
        )}
      >
        {t('emissionFactor.banner.info')}
      </Alert>

      {!loadError && (
        <>
          <EmissionFactorToolbar value={filters.search} onChange={(value) => setFilter('search', value)} />

          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <EmissionFactorTable
              factors={factors}
              filters={filters}
              options={options}
              onFilterChange={setFilter}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={setPage}
              onRowsPerPageChange={setRowsPerPage}
              onEdit={openEdit}
              onHistory={handleHistory}
              onSourceClick={handleSourceClick}
              onStatusChange={handleStatusChange}
              downloadingSourceId={downloadingSourceId}
            />
          </Paper>
        </>
      )}

      <EmissionFactorFormDialog
        open={formOpen}
        factor={editing}
        suppliers={suppliers}
        supplierLoadError={supplierLoadError}
        saving={saving}
        onClose={() => setFormOpen(false)}
        onSubmit={handleSave}
        onStatusChange={handleStatusChange}
      />
      <EmissionFactorSyncDialog
        open={syncOpen}
        policy={syncPolicy}
        isProcessing={isSyncProcessing}
        monitoringError={syncMonitoringError}
        result={syncResult}
        syncHistory={syncHistory}
        onClose={() => setSyncOpen(false)}
        onExited={() => setSyncResult(null)}
        onRetryStatus={handleRetrySyncStatus}
        onSubmit={handleSync}
      />
      <EmissionFactorSyncHistoryDialog
        open={syncHistoryOpen}
        entries={syncHistory}
        loading={syncHistoryLoading}
        onClose={() => setSyncHistoryOpen(false)}
      />
      <AuditHistoryDialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        onExited={() => setHistoryFactor(null)}
        context={historyContext}
        entries={historyFactor ? historyFor(historyFactor) : []}
      />
      <AppToast toast={toast} onClose={() => setToast(null)} />
    </Box>
  );
}
